import { Anchor, Checkbox, NumberFormatter, Text } from "@mantine/core"
import { IconExternalLink } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EcobalyseAnalysisStatus } from "@tfg/types/schemas/enum"
import { FindProductLcasResponse } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/find-product-lcas.response.dto"

import { CodeCell } from "~/components/cells/CodeCell.tsx"
import { DateCell } from "~/components/cells/DateCell.tsx"
import { ScientificNumberCell } from "~/components/cells/ScientificNumberCell.tsx"
import { StatusCell } from "~/pages/Brand/Regulations/LCA/StatusCell.tsx"

const columnHelper =
  createColumnHelper<FindProductLcasResponse["data"][number]>()

export const useLcaColumns = (
  productIds: number[],
  setProductIds: (ids: number[]) => void,
  allProductIds: number[]
) => {
  const { t } = useTranslation("regulations")
  const navigate = useNavigate()

  return useMemo<ColumnDef<FindProductLcasResponse["data"][number], any>[]>(
    () => [
      columnHelper.display({
        id: "select",
        header: () => (
          <Checkbox
            checked={productIds.length === allProductIds.length}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                setProductIds(allProductIds)
              } else {
                setProductIds([])
              }
            }}
            color="blue"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={productIds.includes(row.original.id)}
            onChange={(e) => {
              e.stopPropagation()
              if (e.currentTarget.checked) {
                setProductIds([...productIds, row.original.id])
              } else {
                setProductIds(productIds.filter((id) => id !== row.original.id))
              }
            }}
            color="blue"
          />
        ),
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: t("columns.lca.name"),
        enableSorting: false,
        cell: (info) => (
          <Anchor
            fz="sm"
            c="gray.9"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            {info.getValue()}
          </Anchor>
        ),
      }),
      columnHelper.accessor("brandReference", {
        id: "reference",
        header: t("columns.lca.reference"),
        enableSorting: false,
        cell: (info) => <CodeCell value={info.getValue()} />,
      }),

      columnHelper.accessor("ecobalyseAnalysis", {
        id: "ecs",
        header: t("columns.lca.ecs"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          return value.status === "SUCCESS" ? (
            <NumberFormatter value={value.ecs} decimalScale={0} />
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("ecobalyseAnalysis", {
        id: "pef",
        header: t("columns.lca.pef"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          return value.status === "SUCCESS" ? (
            <ScientificNumberCell value={value.pef} />
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("ecobalyseAnalysis", {
        id: "keyCount",
        header: t("columns.lca.completionIndex"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          return value.status === "SUCCESS" ? (
            <Text>{`${value.percentFilled.toFixed(2)}%`}</Text>
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("ecobalyseAnalysis", {
        id: "status",
        header: t("columns.lca.status"),
        cell: (info) => {
          return <StatusCell analysis={info.getValue()} />
        },
      }),
      columnHelper.accessor("ecobalyseAnalysis", {
        id: "dateCreated",
        header: t("columns.lca.dateCreated"),
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue()
          if (value.status === EcobalyseAnalysisStatus.PENDING) {
            return <Missing />
          } else {
            return <DateCell date={value.dateCreated} />
          }
        },
      }),

      columnHelper.accessor("ecobalyseAnalysis", {
        id: "webUrl",
        header: t("columns.lca.webUrl"),
        enableSorting: false,
        cell: (info) => {
          const value = info.getValue()
          if (value.status !== "SUCCESS" || !value.webUrl) return <Missing />
          else {
            return (
              <Anchor
                href={value.webUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                <IconExternalLink />
              </Anchor>
            )
          }
        },
      }),
    ],
    [t, navigate, productIds, setProductIds, allProductIds]
  )
}
